const notifications = ref<MNotification[]>([])
const hasFetched = ref(false)
const notificationsCount = ref(0)
const unreadNotificationsCount = ref(0)
export default function useNotification() {
  const totalPages = ref(0)
  const currentPage = ref(1)

  const canLoadMore = computed(() => currentPage.value < totalPages.value)
  const toast = useToast()
  const { loggedIn } = useUser()
  const pb = useNuxtApp().$pb
  const isStarting = ref(false)

  async function startNotifications() {
    if (!loggedIn.value || hasFetched.value || isStarting.value) return

    isStarting.value = true
    try {
      await getTotals()
      notifications.value = await getNotifications(1)
      hasFetched.value = true

      await pb.collection('notifications').subscribe('*', async function (e) {
        if (e.action !== 'create') {
          updateNotification(e.record)
          return
        }
        await getTotals()
        updateNotification(e.record)
        toast.add({
          title: 'New Notification',
          icon: 'heroicons:bell-solid',
        })
      })
    }
    catch {
      hasFetched.value = false
    }
    finally {
      isStarting.value = false
    }
  }

  onMounted(startNotifications)

  onUnmounted(() => {
    pb.collection('notifications').unsubscribe()
  })

  function updateNotification(notification?: Notification) {
    if (!notification) return
    const index = notifications.value.findIndex(n => n.id === notification.id)
    if (index === -1) {
      notifications.value.push(notification)
    }
    else {
      notifications.value[index] = notification
    }
  }

  async function getUnreadNotifications() {
    const { items } = await pb.collection('notifications').getList<MNotification>(0, 6, { filter: 'read = false', sort: '-created' })
    return items
  }

  async function getNotifications(pageNumber: number = 1) {
    // const filter = all ? '' : 'read = false'
    const { items, totalPages: totalPagesCount, page } = await pb.collection('notifications').getList<MNotification>(pageNumber, 2, { sort: '-created' })
    currentPage.value = page
    totalPages.value = totalPagesCount
    return items
  }

  async function loadNextPage() {
    if (!canLoadMore.value) return
    const nextPage = currentPage.value + 1
    const newNotifications = await getNotifications(nextPage)
    notifications.value = [...notifications.value, ...newNotifications]
  }

  async function getTotals() {
    const { totalItems } = await pb.collection('notifications').getList(1, 1)
    const { totalItems: unread } = await pb.collection('notifications').getList(1, 1, { filter: 'read = false' })
    notificationsCount.value = totalItems
    unreadNotificationsCount.value = unread
  }

  async function toggleRead(note: Notification) {
    if (!note.id) return
    const read = !note.read
    readMessage(note, read)
  }

  async function readMessage(note: Notification, read: boolean = true) {
    if (!note.id) return
    await pb.collection('notifications').update(note.id, { read })
    if (read) {
      const amnt = unreadNotificationsCount.value
      unreadNotificationsCount.value = amnt > 0 ? amnt - 1 : 0
    }
    else {
      unreadNotificationsCount.value += 1
    }
    updateNotification({ ...note, read })
  }

  watch(loggedIn, (val) => {
    if (val) {
      startNotifications()
      return
    }

    pb.collection('notifications').unsubscribe()
    hasFetched.value = false
    notificationsCount.value = 0
    unreadNotificationsCount.value = 0
    notifications.value = []
  })

  return {
    notifications,
    getNotifications,
    toggleRead,
    readMessage,
    unreadNotificationsCount,
    canLoadMore,
    totalPages,
    loadNextPage,
    getUnreadNotifications,
    notificationsCount,
  }
}
